// Conversation detail (right pane). Header + ordered turns.
//
// The turn renderer is a discriminated union on `kind`. Per-kind extras:
//   - meeting_utterance → MeetingTurnExtras (play button + editable speaker)
//   - slack_message     → SlackTurnExtras (reactions, thread tag, permalink)
//   - email_message     → EmailTurnExtras (subject line, attachments list)
//   - calendar_description → no per-turn extras (CalendarAnnotation
//     surfaces the cross-link at the conversation header instead).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getConversation } from "./api";
import { MeetingTurnExtras } from "./MeetingTurnExtras";
import { SlackTurnExtras } from "./SlackTurnExtras";
import { EmailTurnExtras } from "./EmailTurnExtras";
import { CalendarAnnotation } from "./CalendarAnnotation";
import type { ConversationKind, Turn } from "./types";

const KIND_LABEL: Record<ConversationKind, string> = {
  meeting: "Meeting",
  slack_channel: "Slack channel",
  slack_dm: "Slack DM",
  slack_group_dm: "Slack group DM",
  email_thread: "Email thread",
  calendar_event: "Calendar event",
};

function formatTs(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface Props {
  id: string;
}

export function ConversationDetailPage({ id }: Props): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ["conversation", id],
    queryFn: () => getConversation(id),
  });

  const tsRange = useMemo(() => {
    if (!data) return "";
    if (!data.started_at) return "";
    if (data.ended_at && data.ended_at !== data.started_at) {
      return `${formatTs(data.started_at)} → ${formatTs(data.ended_at)}`;
    }
    return formatTs(data.started_at);
  }, [data]);

  if (isLoading) {
    return <div className="cv-loading">Loading conversation…</div>;
  }
  if (error) {
    return <div className="cv-error">{(error as Error).message}</div>;
  }
  if (!data) {
    return <div className="cv-loading">Not found.</div>;
  }

  return (
    <>
      <div className="cv-detail-header">
        <h2 className="cv-detail-title">{data.title || "(untitled)"}</h2>
        <div className="cv-detail-meta">
          {KIND_LABEL[data.kind]} · {tsRange} · {data.turn_count} turns ·{" "}
          {data.visibility}
        </div>
        {data.participants.length > 0 && (
          <div className="cv-detail-participants">
            {data.participants.map((p, i) => (
              <span key={i} className="cv-participant">
                {p.role !== "with" && (
                  <span className="cv-row-kind">{p.role}</span>
                )}{" "}
                {p.display_name || p.email || p.slack_user_id || p.person_id}
              </span>
            ))}
          </div>
        )}
        <CalendarAnnotation conversation={data} />
      </div>
      <div className="cv-turns">
        {data.turns.length === 0 && (
          <div className="cv-loading">No turns in this conversation.</div>
        )}
        {data.turns.map((t) => (
          <TurnRow key={t.id} turn={t} conversationTitle={data.title} />
        ))}
        {data.turns_has_more && (
          <div className="cv-loading">
            … {data.turn_count - data.turns.length} more turns (pagination
            lands in a follow-up)
          </div>
        )}
      </div>
    </>
  );
}

interface TurnRowProps {
  turn: Turn;
  conversationTitle: string | null;
}

function TurnRow({ turn, conversationTitle }: TurnRowProps): React.ReactElement {
  return (
    <div className="cv-turn">
      <div>
        {turn.kind === "meeting_utterance" ? (
          <MeetingTurnExtras turn={turn} />
        ) : (
          <div className="cv-turn-speaker">
            {turn.speaker_label || "(unknown)"}
          </div>
        )}
        <div className="cv-turn-ts">{formatTs(turn.ts)}</div>
        {turn.kind === "slack_message" && <SlackTurnExtras turn={turn} />}
        {turn.kind === "email_message" && (
          <EmailTurnExtras turn={turn} conversationTitle={conversationTitle} />
        )}
      </div>
      <div className="cv-turn-text">{turn.text}</div>
    </div>
  );
}
